import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useUserData } from '../hooks/useUserData';

const PremiumContext = createContext(null);

export const usePremium = () => {
    const context = useContext(PremiumContext);
    // Return default values if not in provider (safe fallback)
    if (!context) {
        return {
            isPremium: false,
            subscription: null,
            subscriptionEndDate: null,
            loading: false,
            isCanceled: false,
            plan: null,
        };
    }
    return context;
};

export const PremiumProvider = ({ children }) => {
    const { user } = useUserData();
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user?.uid) {
            setSubscription(null);
            setLoading(false);
            return;
        }

        // Listen to user's subscription status in real-time
        const unsubscribe = onSnapshot(
            doc(db, 'users', user.uid),
            (doc) => {
                if (doc.exists()) {
                    const data = doc.data();
                    setSubscription(data.subscription || null);
                } else {
                    setSubscription(null);
                }
                setLoading(false);
            },
            (error) => {
                console.error('Error fetching subscription:', error);
                setSubscription(null);
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [user?.uid]);

    // Check if user has active premium subscription
    const isPremium = React.useMemo(() => {
        if (!subscription) return false;

        const { status, currentPeriodEnd } = subscription;

        // Check if subscription is active
        if (status !== 'active' && status !== 'trialing') return false;

        // Check if subscription hasn't expired
        if (currentPeriodEnd) {
            const endDate = currentPeriodEnd.toDate ? currentPeriodEnd.toDate() : new Date(currentPeriodEnd);
            if (endDate < new Date()) return false;
        }

        return true;
    }, [subscription]);

    // Get subscription end date formatted
    const subscriptionEndDate = React.useMemo(() => {
        if (!subscription?.currentPeriodEnd) return null;
        const endDate = subscription.currentPeriodEnd.toDate
            ? subscription.currentPeriodEnd.toDate()
            : new Date(subscription.currentPeriodEnd);
        return endDate.toLocaleDateString();
    }, [subscription]);

    const value = {
        isPremium,
        subscription,
        subscriptionEndDate,
        loading,
        // Helper flags
        isCanceled: subscription?.cancelAtPeriodEnd === true,
        plan: subscription?.plan || null,
    };

    return (
        <PremiumContext.Provider value={value}>
            {children}
        </PremiumContext.Provider>
    );
};

export default PremiumContext;
